import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { BaseProps } from "@purpur/common-types";

import type { ThemeProps } from "./theme-props";
import type { ColorOptions, ColorScheme, ThemeContextType } from "./types";
import { useApplyTheme } from "./useApplyTheme";

const ThemeContext = createContext<ThemeContextType>({
  colorScheme: "light",
  currentColorScheme: "light",
  setColorScheme: () => {},
  options: {},
  portalContainer: null,
});

type PropsBase = Omit<BaseProps, "children"> & {
  children: ReactNode;
  cssVariablesSelector?: string;
  options?: ThemeContextType["options"];
};

export type Props =
  | PropsBase &
      (
        | {
            theme: { light: ThemeProps; dark?: never };
            forceColorScheme: "light";
          }
        | {
            theme?: { light: ThemeProps; dark: ThemeProps };
            forceColorScheme?: ColorScheme;
          }
      );

export const ThemeProvider = ({
  cssVariablesSelector,
  forceColorScheme,
  theme,
  children,
  options = {},
  ...props
}: Props) => {
  const [colorScheme, setColorScheme] = useState<ColorOptions>(forceColorScheme ?? "auto");
  const [currentColorScheme, setCurrentColorScheme] = useState<ColorScheme>(
    forceColorScheme ?? "light"
  );
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!forceColorScheme) {
      const savedColorScheme = localStorage.getItem("colorScheme") as ColorOptions | null;
      if (savedColorScheme) {
        setColorScheme(savedColorScheme);
      }
    } else {
      setColorScheme(forceColorScheme);
    }
  }, [forceColorScheme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    if (colorScheme === "auto") {
      setCurrentColorScheme(mediaQuery.matches ? "dark" : "light");
    } else {
      setCurrentColorScheme(colorScheme);
    }

    const handleChange = (e: MediaQueryListEvent) => {
      if (!forceColorScheme && colorScheme === "auto") {
        setCurrentColorScheme(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [colorScheme, forceColorScheme]);

  const isCustomTheme = theme !== undefined;

  useApplyTheme({
    theme,
    cssVariablesSelector,
    currentColorScheme: isCustomTheme ? currentColorScheme : undefined,
  });

  const contextValue = useMemo(
    () => ({
      colorScheme,
      currentColorScheme,
      setColorScheme: (scheme: ColorOptions) => {
        if (!forceColorScheme) {
          setColorScheme(scheme);
          localStorage.setItem("colorScheme", scheme);
        }
      },
      portalContainer,
    }),
    [colorScheme, currentColorScheme, forceColorScheme, portalContainer]
  );

  return (
    <ThemeContext.Provider value={{ ...contextValue, options }}>
      <div
        data-color-scheme={currentColorScheme}
        className={currentColorScheme}
        ref={setPortalContainer}
        {...props}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
